import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

// Polling cadence — 5 min while the tab is visible. Balances "users see the
// new version quickly after a deploy" against "don't hammer /index.html".
const POLL_INTERVAL_MS = 5 * 60 * 1000;

// Grace period before the first probe. Avoids racing with the initial
// bundle load and gives the user a fresh minute after arriving.
const INITIAL_DELAY_MS = 60 * 1000;

// Matches CRA/webpack 5's `main.<8-hex>.js` and any hashed chunk.
const MAIN_ASSET_RE = /\/static\/js\/main\.[a-f0-9]{6,}\.js/;

/** Extract the current `main.<hash>.js` URL from a document. Returns null
 *  if we're not looking at a hashed production build (dev preview). */
function extractMainAsset(doc) {
  const scripts = doc.querySelectorAll('script[src*="/static/js/main."]');
  for (const s of scripts) {
    const src = s.getAttribute("src") || "";
    if (MAIN_ASSET_RE.test(src)) return src.split("?")[0];
  }
  return null;
}

/**
 * VersionWatcher — silently polls `/index.html` for a new deploy and shows a
 * one-time "New version available" toast when the main bundle hash changes.
 *
 * Design principles:
 *   • **Zero noise in dev**. Skips entirely if the current page isn't a
 *     hashed production build (no `main.<hash>.js` on the DOM).
 *   • **Only poll when the tab is visible.** No background traffic when the
 *     user isn't looking.
 *   • **Show the toast once per session.** No respawning every 5 min.
 *   • **User controls the reload.** Never force-refresh; they may have
 *     unsaved work.
 */
export default function VersionWatcher() {
  const currentAssetRef = useRef(null);
  const notifiedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const currentAsset = extractMainAsset(document);
    // Dev preview / non-hashed build → nothing to watch.
    if (!currentAsset) return;
    currentAssetRef.current = currentAsset;

    const check = async () => {
      if (notifiedRef.current || document.hidden) return;
      try {
        // Cache-bust the probe itself so intermediate caches can't lie to
        // us. `_headers` sets no-cache on /index.html but this is belt-
        // and-braces for CDNs that ignore file-based header rules.
        const res = await fetch(`/index.html?v=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!res.ok) return;
        const html = await res.text();
        const parser = new DOMParser();
        const remoteDoc = parser.parseFromString(html, "text/html");
        const remoteAsset = extractMainAsset(remoteDoc);
        if (!remoteAsset || remoteAsset === currentAssetRef.current) return;

        notifiedRef.current = true;
        toast("A new version is available", {
          description: "Refresh to pick up the latest improvements.",
          duration: Infinity, // sticky until dismissed or actioned
          dismissible: true,
          action: {
            label: "Refresh",
            onClick: () => window.location.reload(),
          },
          icon: <RefreshCw className="h-4 w-4 text-primary" />,
          className: "border-primary/30 bg-card",
        });
      } catch (_) {
        // Offline / CDN hiccup — try again next tick. Never surface to user.
      }
    };

    // Fire the first check after the initial delay, then on interval.
    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(check, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const initial = setTimeout(() => {
      start();
      // Also probe immediately when the user comes back to the tab —
      // a returning visitor after a long lunch is the most common
      // "stuck on old build" scenario.
      check();
    }, INITIAL_DELAY_MS);

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
        check();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(initial);
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
}
